import { ApiConfig, AuthDeniedHandlers } from './contracts';
import axios, {
  AxiosInstance,
  ResponseType,
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
} from 'axios';
import {
  AxiosCacheInstance,
  setupCache,
  buildWebStorage,
} from 'axios-cache-interceptor';

import { AppConstants, uncompressLzwObject } from '@ssar-app/domain';

export interface AxiosOktaSessionConfig {
  getAccessToken: () => string | undefined | null;
}

const axiosCacheStorage = buildWebStorage(
  localStorage,
  'axios-cache-storage-ssar',
);

let axiosOktaSessionConfig: AxiosOktaSessionConfig | undefined = undefined;

export const setAxiosOktaSessionConfig = (
  config: AxiosOktaSessionConfig,
): void => {
  axiosOktaSessionConfig = config;
};

type InterceptorAdder<T extends AxiosInstance> = (axiosInstance: T) => T;

const withLzwDecompressionInterceptor = <T extends AxiosInstance>(
  axiosInstance: T,
): T => {
  axiosInstance.interceptors.response.use((response) => {
    const lzwHeaderValue =
      response.headers[AppConstants.LzwCompressionResponseHeaderName] ||
      response.headers[
        AppConstants.LzwCompressionResponseHeaderName.toUpperCase()
      ];
    if (lzwHeaderValue === '1') {
      if (response.data?.data) {
        response.data.data = uncompressLzwObject(response.data.data);
      }
    }
    return response;
  });
  return axiosInstance;
};

const withAuthRequestInterceptor = <T extends AxiosInstance>(
  axiosInstance: T,
): T => {
  axiosInstance.interceptors.request.use(function (config) {
    const accessToken = axiosOktaSessionConfig?.getAccessToken();
    if (accessToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
      };
    }

    return config;
  });
  return axiosInstance;
};

const buildNeverResolvedPromise = (
  fn: () => void,
  shouldRejectIfFnFails = true,
) => {
  return new Promise((_, reject) => {
    try {
      fn();
    } catch (err) {
      if (shouldRejectIfFnFails) {
        reject(err);
      } else {
        console.error(err);
      }
    }
  });
};

const with401403ResponseInterceptor =
  (authDeniedHandlers?: AuthDeniedHandlers) =>
  <T extends AxiosInstance>(axiosInstance: T): T => {
    axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const { onAuthenticationError, onAuthorizationError } =
          authDeniedHandlers || {};
        if (401 === error?.response?.status && onAuthenticationError) {
          return buildNeverResolvedPromise(onAuthenticationError);
        } else if (403 === error?.response?.status && onAuthorizationError) {
          return buildNeverResolvedPromise(onAuthorizationError);
        } else if (error.response) {
          return Promise.reject(error.response.data);
        }
        return Promise.reject(error);
      },
    );
    return axiosInstance;
  };

const addInterceptors =
  <T extends AxiosInstance>(...interceptors: InterceptorAdder<T>[]) =>
  (axiosInstance: T): T => {
    interceptors.reduce((acc, curr) => {
      curr(acc);
      return acc;
    }, axiosInstance);
    return axiosInstance;
  };

class AxiosProvider {
  private apiInstance?: AxiosCacheInstance;
  private apiConfig: ApiConfig | null = null;
  private authDeniedHandlers?: AuthDeniedHandlers;

  public setAuthDeniedHandlers(authDeniedHandlers: AuthDeniedHandlers): void {
    this.authDeniedHandlers = authDeniedHandlers;
  }
  public setApiConfig(apiConfig: ApiConfig): void {
    this.apiConfig = apiConfig;
  }

  public buildApiUrl(uri: string): string {
    return `${this.apiConfig?.apiRoot}${uri}`;
  }

  public get apiClient(): AxiosCacheInstance {
    if (!this.apiConfig) {
      throw new Error('Api Config is not initialized');
    } else {
      if (!this.apiInstance) {
        const requestConfig: AxiosRequestConfig = {
          baseURL: this.apiConfig?.apiRoot,
          responseType: 'json' as ResponseType,
          headers: {
            'Content-Type': 'application/json',
          },
          xsrfCookieName: 'XSRF-TOKEN',
          xsrfHeaderName: 'XSRF-TOKEN',
          withCredentials: true,
        };
        const axiosInstance = addInterceptors(
          withAuthRequestInterceptor,
          with401403ResponseInterceptor(this.authDeniedHandlers),
          withLzwDecompressionInterceptor,
        )(axios.create(requestConfig));
        this.apiInstance = setupCache(axiosInstance, {
          interpretHeader: true,
          storage: axiosCacheStorage,
          ttl: 0,
        });
      }
      return this.apiInstance;
    }
  }
}

export const unwrapResponseData = <T>(
  response: AxiosResponse<T>,
): Promise<T> => {
  return Promise.resolve(response.data);
};

export const axiosProvider = new AxiosProvider();
